import { formatPropertyUnitLabel } from "../../../../../../../../../../../../../_shared/_logic/unit-labels"
import type {
  WorkOrderLetterAddress,
  WorkOrderPrintMode,
} from "../../../../../../../../../../../../../api/kehrblatt/letters"

type AppointmentPropertyInput = {
  propertyAddress?: WorkOrderLetterAddress | null
  propertyLabel?: string | null
  unitLabel?: string | null
}

type RecipientDisplayInput = {
  mode?: WorkOrderPrintMode | "" | null
  recipientName?: string | null
  recipientOptionLabel?: string | null
  recipientAddress?: WorkOrderLetterAddress | null
  propertyAddress?: WorkOrderLetterAddress | null
}

export type LetterDeliveryRecipientDisplay = {
  label: string
  addressLabel: string
  title: string
}

const cleanText = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

const addressStreetLine = (address?: WorkOrderLetterAddress | null) =>
  [address?.street, address?.house_number]
    .map((value) => cleanText(value))
    .filter(Boolean)
    .join(" ")

const addressCityLine = (address?: WorkOrderLetterAddress | null) =>
  [address?.zip, address?.city]
    .map((value) => cleanText(value))
    .filter(Boolean)
    .join(" ")

export const formatLetterDeliveryAddressLabel = (
  address?: WorkOrderLetterAddress | null,
): string => {
  const lines = (address?.lines ?? []).map((value) => cleanText(value)).filter(Boolean)

  if (lines.length) return lines.join(", ")

  return [
    addressStreetLine(address),
    cleanText(address?.address_extra),
    addressCityLine(address),
    cleanText(address?.country),
  ]
    .filter(Boolean)
    .join(", ")
}

export const formatAppointmentPropertySubLabel = ({
  propertyAddress,
  propertyLabel,
  unitLabel,
}: AppointmentPropertyInput): string => {
  const addressLabel =
    addressStreetLine(propertyAddress) ||
    cleanText(propertyLabel) ||
    cleanText(propertyAddress?.label)
  const unit = unitLabel ? formatPropertyUnitLabel(unitLabel) : "Gebäudeeinheit"

  return `${addressLabel || "Liegenschaft"} · ${unit}`
}

export const buildLetterDeliveryRecipientDisplay = ({
  mode,
  recipientName,
  recipientOptionLabel,
  recipientAddress,
  propertyAddress,
}: RecipientDisplayInput): LetterDeliveryRecipientDisplay => {
  const isPersonal = mode === "PERSONAL_LETTER"
  const label =
    mode === "GENERIC_KLEBEZETTEL"
      ? "Aushang"
      : mode === "IMPERSONAL_LETTER"
        ? "Unpersönlich"
        : cleanText(recipientName, recipientOptionLabel) || "Empfänger"
  const addressLabel =
    formatLetterDeliveryAddressLabel(isPersonal ? recipientAddress : propertyAddress) ||
    "Keine Zieladresse"

  return {
    label,
    addressLabel,
    title: `${label} · ${addressLabel}`,
  }
}
